import { useParams, Link } from "react-router-dom";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/lib/supabase";

interface Project {
  id: string;
  title_en: string;
  title_ps: string;
  title_da: string;
  description_en: string;
  description_ps: string;
  description_da: string;
  category: string;
  status: string;
  image_url: string;
  location: string;
  beneficiaries: number;
  progress: number;
  created_at: string;
}

const categoryColors: Record<string, string> = {
  Education: "bg-amber-100 text-amber-700",
  Infrastructure: "bg-sky-100 text-sky-700",
  Healthcare: "bg-rose-100 text-rose-700",
  "Emergency Relief": "bg-orange-100 text-orange-700",
  "Women Empowerment": "bg-violet-100 text-violet-700",
};

function getLocalizedField(project: Project, field: string, lang: string): string {
  const suffix = lang === "ps" ? "_ps" : lang === "da" ? "_da" : "_en";
  return (project as any)[field + suffix] || (project as any)[field + "_en"] || "";
}

export default function ProjectDetailPage() {
  const { t, i18n } = useTranslation();
  const { id } = useParams<{ id: string }>();
  const [project, setProject] = useState<Project | null>(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!id) { setLoading(false); return; }
    supabase
      .from("projects")
      .select("*")
      .eq("id", id)
      .maybeSingle()
      .then(({ data, error }) => {
        if (error) console.error("Project detail fetch error:", error.message);
        setProject((data as Project) ?? null);
        setLoading(false);
      });
  }, [id]);

  if (loading) {
    return (
      <main className="pt-32 pb-20 text-center">
        <div className="w-8 h-8 border-2 border-[#3B9EF5] border-t-transparent rounded-full animate-spin mx-auto"></div>
      </main>
    );
  }

  if (!project) {
    return (
      <main className="pt-32 pb-20 text-center">
        <i className="ri-folder-open-line text-5xl text-gray-300 mb-4 block"></i>
        <h2 className="text-2xl font-bold text-gray-600 mb-4">{t("projectDetail.notFound")}</h2>
        <Link to="/projects" className="text-[#3B9EF5] hover:underline">{t("projectDetail.notFound.back")}</Link>
      </main>
    );
  }

  return (
    <main className="pt-24">
      {/* Breadcrumb */}
      <div className="bg-white border-b border-gray-100">
        <div className="max-w-7xl mx-auto px-4 md:px-6 py-3 flex items-center gap-2 text-sm text-gray-500">
          <Link to="/" className="hover:text-[#3B9EF5] cursor-pointer">{t("projectDetail.home")}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <Link to="/projects" className="hover:text-[#3B9EF5] cursor-pointer">{t("projectDetail.projects")}</Link>
          <i className="ri-arrow-right-s-line"></i>
          <span className="text-[#1B2838] font-medium">{getLocalizedField(project, "title", i18n.language)}</span>
        </div>
      </div>

      <section className="py-14 bg-[#FAF8F5]">
        <div className="max-w-7xl mx-auto px-4 md:px-6">
          <div className="grid grid-cols-1 lg:grid-cols-3 gap-10">
            {/* Main */}
            <div className="lg:col-span-2">
              <div className="relative rounded-xl overflow-hidden mb-8 h-56 sm:h-72 md:h-80">
                <img src={project.image_url} alt={getLocalizedField(project, "title", i18n.language)} className="w-full h-full object-cover object-top" />
                <div className="absolute top-4 left-4">
                  <span className={`text-xs font-semibold px-3 py-1.5 rounded-full ${categoryColors[project.category] || "bg-gray-100 text-gray-600"}`}>
                    {project.category}
                  </span>
                </div>
              </div>

              <h1 className="text-3xl font-bold text-[#1B2838] mb-4" style={{ fontFamily: "'Playfair Display', serif" }}>
                {getLocalizedField(project, "title", i18n.language)}
              </h1>

              <div className="flex flex-wrap gap-4 mb-6 text-sm text-gray-500">
                <span className="flex items-center gap-1"><i className="ri-map-pin-line text-[#3B9EF5]"></i>{project.location}</span>
                <span className="flex items-center gap-1"><i className="ri-group-line text-[#3B9EF5]"></i>{project.beneficiaries.toLocaleString()} {t("projectDetail.beneficiariesSuffix")}</span>
                <span className={`flex items-center gap-1 px-2 py-0.5 rounded-full text-xs font-semibold ${project.status === "completed" ? "bg-green-100 text-green-700" : "bg-[#3B9EF5]/15 text-[#3B9EF5]"}`}>
                  {project.status === "completed" ? t("projectDetail.details.statusCompleted") : t("projectDetail.details.statusActive")}
                </span>
              </div>

              <div className="prose prose-gray max-w-none">
                <p className="text-gray-600 leading-relaxed text-base mb-4">{getLocalizedField(project, "description", i18n.language)}</p>
                <p className="text-gray-600 leading-relaxed text-base mb-4">
                  {t("projectDetail.body1")}
                </p>
                <p className="text-gray-600 leading-relaxed text-base">
                  {t("projectDetail.body2")}
                </p>
              </div>
            </div>

            {/* Sidebar */}
            <div className="space-y-5">
              {/* Funding */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="font-bold text-[#1B2838] mb-4">{t("projectDetail.funding.title")}</h3>
                <div className="flex items-center gap-3 mb-4">
                  <div className="w-14 h-14 flex items-center justify-center rounded-full bg-[#3B9EF5]/10 flex-shrink-0">
                    <span className="text-2xl font-bold text-[#3B9EF5]">{project.progress}%</span>
                  </div>
                  <div>
                    <div className="text-sm font-semibold text-[#1B2838]">{t("projectDetail.funding.complete")}</div>
                    <div className="text-xs text-gray-400">{project.beneficiaries.toLocaleString()} {t("projectDetail.details.beneficiaries")}</div>
                  </div>
                </div>
                <div className="h-2 bg-gray-100 rounded-full overflow-hidden mb-2">
                  <div className="h-full bg-[#3B9EF5] rounded-full" style={{ width: `${Math.min(project.progress, 100)}%` }}></div>
                </div>
                <div className="text-[#3B9EF5] font-bold text-sm mb-5">{project.progress}% {t("projectDetail.funding.funded")}</div>
                <Link
                  to="/donate"
                  className="block text-center py-3 bg-[#3B9EF5] text-[#1B2838] rounded-full font-bold text-sm whitespace-nowrap cursor-pointer hover:bg-[#2A8DE0] transition-colors"
                >
                  {t("projectDetail.funding.cta")}
                </Link>
              </div>

              {/* Details */}
              <div className="bg-white rounded-xl border border-gray-100 p-6">
                <h3 className="font-bold text-[#1B2838] mb-4">{t("projectDetail.details.title")}</h3>
                <div className="space-y-3 text-sm">
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t("projectDetail.details.category")}</span>
                    <span className="font-medium text-[#1B2838]">{project.category}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t("projectDetail.details.location")}</span>
                    <span className="font-medium text-[#1B2838]">{project.location}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t("projectDetail.details.beneficiaries")}</span>
                    <span className="font-medium text-[#1B2838]">{project.beneficiaries.toLocaleString()}</span>
                  </div>
                  <div className="flex justify-between">
                    <span className="text-gray-500">{t("projectDetail.details.status")}</span>
                    <span className={`font-medium ${project.status === "completed" ? "text-green-600" : "text-[#3B9EF5]"}`}>
                      {project.status === "completed" ? t("projectDetail.details.statusCompleted") : t("projectDetail.details.statusActive")}
                    </span>
                  </div>
                </div>
              </div>

              <Link
                to="/projects"
                className="flex items-center gap-2 text-gray-500 text-sm hover:text-[#1B2838] cursor-pointer"
              >
                <i className="ri-arrow-left-line"></i>
                {t("projectDetail.back")}
              </Link>
            </div>
          </div>
        </div>
      </section>
    </main>
  );
}